import { useState } from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  useGetList,
  useNotify,
  useStore,
} from "react-admin";
import { EmptyList } from "server/components";
import CustomFilter from "features/CustomFilter";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { DoctoralServicePath, MOBILE_BREAKPOINT } from "utils/constants";
import {
  DoctorantCandidacyStatusChoices,
  DoctorantCandidacyStatusColorMap,
} from "utils/choices";
import StatusChip from "../Partials/StatusChip";
import { doctoralSeatLabel } from "../seatLabel";
import DoctoralDateField from "../Partials/DoctoralDateField";

const candidaciesResource = `${DoctoralServicePath}doctorant-candidacies`;
const campaignsResource = `${DoctoralServicePath}doctoral-campaigns`;
const CANDIDACIES_CAMPAIGN_STORE_KEY = "doctorantCandidaciesCampaign.filter";

const ListDoctorantCandidacies = () => {
  const notify = useNotify();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // filters drives the <List> and the gate (persisted via useStore);
  // campaignId is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(CANDIDACIES_CAMPAIGN_STORE_KEY, null);
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);

  const { data: campaignsData = [] } = useGetList(campaignsResource, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "dateFrom", order: "DESC" },
  });

  const campaignChoices = campaignsData.map(c => ({
    id: c.id,
    name: c.nameBg,
  }));

  const onSubmit = () => {
    if (!campaignId) {
      notify("Моля въведете кампания!", { type: "error" });
      return;
    }
    setFilters({ campaignId });
  };

  return (
    <>
      {!filters && <Title title="Кандидати" />}
      <CustomFilter
        title="Списък кампании"
        cardWidth="700px"
        buttonLabel="Провери"
        buttonOnClick={onSubmit}
        filters={[
          {
            label: "Кампания",
            value: campaignId,
            setValue: setCampaignId,
            type: "autocomplete",
            multiple: false,
            choices: campaignChoices,
          },
        ]}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography variant="inherit" align="center" color="secondary">
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          resource={candidaciesResource}
          filter={filters}
          actions={false}
          disableSyncWithLocation
          title="Кандидати"
          sx={settings.style.blockStyle}
          empty={<EmptyList />}
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Вх. номер", value: record?.registrationNumber }]} />
                  <MobileListRow
                    fields={[
                      { label: "Име", value: record?.firstName },
                      { label: "Презиме", value: record?.secondName },
                      { label: "Фамилия", value: record?.lastName },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "ID", value: record?.id }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Дата на кандидатура",
                        value: <DoctoralDateField source="createdOn" showTime />,
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Места",
                        value: record?.seats?.length
                          ? record.seats.map(s => `№${s.priority} ${doctoralSeatLabel(s)}`).join(", ")
                          : "—",
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус",
                        value: (
                          <StatusChip
                            status={record.status}
                            choices={DoctorantCandidacyStatusChoices}
                            colorMap={DoctorantCandidacyStatusColorMap}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[{ label: "Обработва се от", value: record?.assignedToUserName ?? "—" }]}
                  />
                </div>
              )}
              tertiaryText={() => <ShowButton label="" />}
            />
          ) : (
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <TextField source="id" label="ID" sortable={false} />
              <TextField source="registrationNumber" label="Вх. номер" sortable={false} />
              <TextField source="firstName" label="Име" sortable={false} />
              <TextField source="secondName" label="Презиме" sortable={false} />
              <TextField source="lastName" label="Фамилия" sortable={false} />
              <DoctoralDateField source="createdOn" label="Дата на кандидатура" showTime />
              <FunctionField
                label="Места"
                sortable={false}
                render={r =>
                  r.seats?.length
                    ? r.seats.map(s => `№${s.priority} ${doctoralSeatLabel(s)}`).join(", ")
                    : "—"
                }
              />
              <FunctionField
                label="Статус"
                render={record => (
                  <StatusChip
                    status={record.status}
                    choices={DoctorantCandidacyStatusChoices}
                    colorMap={DoctorantCandidacyStatusColorMap}
                  />
                )}
              />
              <TextField
                source="assignedToUserName"
                label="Обработва се от"
                emptyText="—"
                sortable={false}
              />
              <ShowButton label="Детайли" />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListDoctorantCandidacies;
